import { Bell, CheckCircle2, Eye, EyeOff, Link, Loader2, Lock, Mail, Pencil, Settings, User } from 'lucide-react'
import { useEffect, useState } from 'react'
import GoogleIcon from '../components/icons/GoogleIcon'
import Layout from '../components/Layout'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Toggle } from '../components/ui/toggle'
import { useAppNotifications } from '../contexts/AppNotificationsContext'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { useUser } from '../contexts/UserContext'
import AvatarPicker from '../components/AvatarPicker'
import { Avatar } from '../components/ui/avatar'
import { phoneSchema } from '../lib/schemas'
import { updateUserProfile } from '../services/userService'

const NOTIFICATION_SETTINGS = [
  { key: 'bracket_updates_enabled', title: 'Bracket Updates', description: 'Get notified when official results are updated for brackets you\'ve entered' },
  { key: 'prize_notifications_enabled', title: 'Prize Notifications', description: 'Get notified when you win a prize' },
  { key: 'group_notifications_enabled', title: 'Group Notifications', description: 'Get notified about group invites, activity and kudos' },
  { key: 'new_tournament_enabled', title: 'New Tournaments', description: 'Get notified when a new tournament is published' },
  { key: 'picks_open_enabled', title: 'Picks Open', description: 'Get notified when brackets open for picks' },
  { key: 'lock_reminder_enabled', title: 'Lock Reminders', description: 'Get a reminder before a bracket closes so you can submit your picks' },
  { key: 'streak_milestone_enabled', title: 'Streak Milestones', description: 'Get notified when you hit a streak milestone' },
  { key: 'group_messages_enabled', title: 'Group Messages', description: 'Get notified when a group owner sends a message' },
  { key: 'push_enabled', title: 'Push Notifications', description: 'Receive push notifications on your device (coming soon)' },
]

export default function UserSettings() {
  const notify = useNotification()
  const { updatePassword, linkWithGoogle, getLinkedProviders, user: authUser } = useAuth()
  const { user, fetchUserProfile } = useUser()
  const { settings: notificationSettings, updateSettings: updateNotificationSettings, fetchSettings } = useAppNotifications()

  // Get linked providers from auth user
  const linkedProviders = getLinkedProviders()

  // Notification settings state
  const [savingNotificationSettings, setSavingNotificationSettings] = useState(false)

  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false)
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    city: '',
    stateCode: '',
    country: '',
    phone: '',
  })
  const [isSavingProfile, setIsSavingProfile] = useState(false)
  const [isAvatarPickerOpen, setIsAvatarPickerOpen] = useState(false)

  // US States for dropdown
  const US_STATES = [
    { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }, { code: 'DC', name: 'Washington D.C.' }
  ]

  const getStateName = (code) => US_STATES.find(s => s.code === code)?.name || code
  const getCountryName = (code) => ({ 'USA': 'United States', 'CAN': 'Canada', 'MEX': 'Mexico', 'OTHER': 'Other' }[code] || code)

  const startEditingProfile = () => {
    setProfileForm({
      firstName: user?.first_name || '',
      lastName: user?.last_name || '',
      city: user?.city || '',
      stateCode: user?.state_code || '',
      country: user?.country || 'USA',
      phone: user?.phone || '',
    })
    setIsEditingProfile(true)
  }

  const cancelEditingProfile = () => {
    setIsEditingProfile(false)
  }

  const handleSaveProfile = async () => {
    if (!profileForm.firstName.trim() || !profileForm.lastName.trim()) {
      notify.error('First name and last name are required')
      return
    }
    if (!profileForm.city.trim() || !profileForm.stateCode || !profileForm.country) {
      notify.error('City, state, and country are required')
      return
    }

    const trimmedPhone = (profileForm.phone || '').trim()
    if (trimmedPhone) {
      const phoneResult = phoneSchema.safeParse(trimmedPhone)
      if (!phoneResult.success) {
        notify.error(phoneResult.error.issues[0]?.message || 'Enter a valid phone number')
        return
      }
    }

    setIsSavingProfile(true)
    try {
      await updateUserProfile(user.id, {
        first_name: profileForm.firstName.trim(),
        last_name: profileForm.lastName.trim(),
        city: profileForm.city.trim(),
        state_code: profileForm.stateCode,
        country: profileForm.country,
        phone: trimmedPhone || null,
      })

      await fetchUserProfile()
      notify.success('Profile updated successfully!')
      setIsEditingProfile(false)
    } catch (err) {
      console.error('Error updating profile:', err)
      notify.error(err.message || 'Failed to update profile')
    } finally {
      setIsSavingProfile(false)
    }
  }


  // Password change state
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)

  const handleChangePassword = async (e) => {
    e.preventDefault()

    // Validation
    if (!newPassword || !confirmPassword) {
      notify.error('Please fill in all password fields')
      return
    }

    if (newPassword.length < 6) {
      notify.error('Password must be at least 6 characters')
      return
    }

    if (newPassword !== confirmPassword) {
      notify.error('New passwords do not match')
      return
    }

    setIsChangingPassword(true)
    try {
      await updatePassword(newPassword)
      notify.success('Password updated successfully!')
      // Clear form
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (error) {
      console.error('Error updating password:', error)
      notify.error(error.message || 'Failed to update password')
    } finally {
      setIsChangingPassword(false)
    }
  }

  // Handle notification setting toggle
  const handleNotificationSettingChange = async (setting, value) => {
    setSavingNotificationSettings(true)
    try {
      const success = await updateNotificationSettings({
        ...notificationSettings,
        [setting]: value
      })
      if (success) {
        notify.success('Notification settings updated')
      } else {
        notify.error('Failed to update notification settings')
      }
    } catch (err) {
      console.error('Error updating notification settings:', err)
      notify.error('Failed to update notification settings')
    } finally {
      setSavingNotificationSettings(false)
    }
  }

  // Fetch notification settings on mount
  useEffect(() => {
    fetchSettings()
  }, [fetchSettings])

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Settings className="h-8 w-8" />
            <h1 className="text-3xl font-bold">Settings</h1>
          </div>
          <p className="text-muted-foreground">Manage your account settings</p>
        </div>

        <div className="space-y-6">
          {/* Profile Information */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  <CardTitle>Profile Information</CardTitle>
                </div>
                {!isEditingProfile && (
                  <Button variant="outline" size="sm" onClick={startEditingProfile}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                )}
              </div>
              <CardDescription>Your personal information</CardDescription>
            </CardHeader>
            <CardContent>
              {isEditingProfile ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">First Name</label>
                      <Input
                        value={profileForm.firstName}
                        onChange={(e) => setProfileForm({ ...profileForm, firstName: e.target.value })}
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Last Name</label>
                      <Input
                        value={profileForm.lastName}
                        onChange={(e) => setProfileForm({ ...profileForm, lastName: e.target.value })}
                        placeholder="Doe"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">City</label>
                    <Input
                      value={profileForm.city}
                      onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
                      placeholder="Austin"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Phone (optional)</label>
                    <Input
                      type="tel"
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">State</label>
                      <Select value={profileForm.stateCode || ''} onValueChange={(val) => setProfileForm({ ...profileForm, stateCode: val })}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          {US_STATES.map((state) => (
                            <SelectItem key={state.code} value={state.code}>
                              {state.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Country</label>
                      <Select value={profileForm.country} onValueChange={(val) => setProfileForm({ ...profileForm, country: val })}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="USA">United States</SelectItem>
                          <SelectItem value="CAN">Canada</SelectItem>
                          <SelectItem value="MEX">Mexico</SelectItem>
                          <SelectItem value="OTHER">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button onClick={handleSaveProfile} disabled={isSavingProfile}>
                      {isSavingProfile ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </Button>
                    <Button variant="outline" onClick={cancelEditingProfile} disabled={isSavingProfile}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <Avatar
                      size="lg"
                      fullName={user?.full_name || [user?.first_name, user?.last_name].filter(Boolean).join(' ')}
                      avatarUrl={user?.avatar_url}
                      avatarType={user?.avatar_type}
                    />
                    <Button variant="outline" size="sm" onClick={() => setIsAvatarPickerOpen(true)}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Change avatar
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-muted-foreground">First Name</span>
                      <p className="font-medium">{user?.first_name || 'Not set'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Last Name</span>
                      <p className="font-medium">{user?.last_name || 'Not set'}</p>
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">City</span>
                    <p className="font-medium">{user?.city || 'Not set'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Phone</span>
                    <p className="font-medium">{user?.phone || 'Not set'}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-muted-foreground">State</span>
                      <p className="font-medium">{user?.state_code ? getStateName(user.state_code) : 'Not set'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Country</span>
                      <p className="font-medium">{user?.country ? getCountryName(user.country) : 'Not set'}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Account Info */}
          <Card>
            <CardHeader>
              <CardTitle>Account</CardTitle>
              <CardDescription>Your account information</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <span className="text-sm text-muted-foreground">Email</span>
                  <p className="font-medium">{user?.email || 'Not available'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Linked Accounts */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Link className="h-5 w-5" />
                <CardTitle>Linked Accounts</CardTitle>
              </div>
              <CardDescription>Manage how you sign in to your account</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Email/Password */}
                <div className="flex flex-col gap-3 p-3 rounded-lg border sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      <Mail className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium">Email & Password</p>
                      <p className="text-sm text-muted-foreground truncate">
                        {linkedProviders.includes('email') ? user?.email : 'Not connected'}
                      </p>
                    </div>
                  </div>
                  {linkedProviders.includes('email') ? (
                    <span className="text-sm text-[var(--teal-11)] font-medium flex items-center gap-1 flex-shrink-0">
                      <CheckCircle2 className="h-4 w-4" />
                      Connected
                    </span>
                  ) : (
                    <span className="text-sm text-muted-foreground flex-shrink-0">Not available</span>
                  )}
                </div>

                {/* Google */}
                <div className="flex flex-col gap-3 p-3 rounded-lg border sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      <GoogleIcon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium">Google</p>
                      <p className="text-sm text-muted-foreground truncate">
                        {linkedProviders.includes('google')
                          ? authUser?.identities?.find(i => i.provider === 'google')?.identity_data?.email || 'Connected'
                          : 'Not connected'}
                      </p>
                    </div>
                  </div>
                  {linkedProviders.includes('google') ? (
                    <span className="text-sm text-[var(--teal-11)] font-medium flex items-center gap-1 flex-shrink-0">
                      <CheckCircle2 className="h-4 w-4" />
                      Connected
                    </span>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-shrink-0"
                      onClick={async () => {
                        try {
                          await linkWithGoogle()
                        } catch (err) {
                          notify.error(err.message || 'Failed to link Google account')
                        }
                      }}
                    >
                      Link Account
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Change Password */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                <CardTitle>Change Password</CardTitle>
              </div>
              <CardDescription>Update your password to keep your account secure</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">New Password</label>
                  <div className="relative">
                    <Input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Confirm New Password</label>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={isChangingPassword} className="w-full sm:w-auto">
                  {isChangingPassword ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Notification Settings */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                <CardTitle>Notifications</CardTitle>
              </div>
              <CardDescription>Choose what notifications you receive</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {NOTIFICATION_SETTINGS.map(({ key, title, description }) => (
                  <div key={key} className="flex items-start justify-between gap-4 p-3 rounded-lg border">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">{title}</p>
                      <p className="text-sm text-muted-foreground">{description}</p>
                    </div>
                    <Toggle
                      checked={notificationSettings?.[key] ?? false}
                      onCheckedChange={(checked) => handleNotificationSettingChange(key, checked)}
                      disabled={savingNotificationSettings || !notificationSettings}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <AvatarPicker
        open={isAvatarPickerOpen}
        onOpenChange={setIsAvatarPickerOpen}
        currentAvatarType={user?.avatar_type}
        currentAvatarUrl={user?.avatar_url}
        userId={user?.id}
        googleAvatarUrl={authUser?.identities?.find(i => i.provider === 'google')?.identity_data?.avatar_url}
        onSaved={() => fetchUserProfile()}
      />
    </Layout>
  )
}
